/* eslint-disable indent */
import { html, css, LitElement, nothing } from 'lit';
import { customElement, state, query, property } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import { loadElearning, loadElearnings, loadFilters } from '../../store/elearning/elearning.js';
import { translate } from '../../helpers/translate.js';
import {
    toSelectItems,
    getSelectedItemValue,
    toSelectedItemValue,
 } from '../../helpers/controls.js';
 import { getPlayerTypeLabel } from '../../store/elearning/helpers.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/paper-listbox/paper-listbox.js';
import '@polymer/paper-item/paper-icon-item.js';
import '@polymer/paper-item/paper-item-body.js';
import '../shared/sdb-dialog.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-search-input.js';

@customElement('sdb-elearning-select')
class SdbElearningSelect extends connect(store)(LitElement) {

    @state()
    accessor isGlobal = false;

    @state()
    accessor items;

    @state()
    accessor total;

    @state()
    accessor filters = {};

    @state()
    accessor playerTypes = [];

    @state()
    accessor suppliers = [];

    @state()
    accessor isLoadingFilters = false;

    @state()
    accessor isLoadingInitialList = false;

    @state()
    accessor selectedListItem = null;

    @query('#search')
    accessor searchInput;

    @query('#el_list')
    accessor listbox;

    @property({type: String})
    accessor launchId = null;

     @property({type: String})
     accessor _selectedItem = null;

    stateChanged(state) {
        this.isGlobal = state.env.hasGlobalId;
        this.items = state.elearning.list;
        this.total = state.elearning.total;
        this.filters = state.elearning.filters;
        this._selectedItem = state.elearning.current;
        this.playerTypes = state.elearning.playerTypes || [];
        this.suppliers = state.elearning.suppliers || [];
        this.isLoadingFilters = state.elearning.status === 'loadingFilters' || !state.elearning.playerTypes;
        this.isLoadingInitialList = state.elearning.status === 'loading' && state.page === -1;
    }

    static get styles() {
        return css`
            .filter-bar {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .filter-bar sdb-search-input {
                margin-top: 3px;
                width: 50%;
            }

            .filter-bar .filters {
                display: flex;
                justify-content: space-between;
                width: 40%;
            }

            .filters #supplier_filter {
                width: 50%;
            }

            .filters #playertype_filter {
                width: 40%;
            }

            .list {
                height: 550px;
                width: 900px;
                overflow-y: auto;
            }

            #el_list {
                contain: none !important; /* lit-virtualizer hack */
            }

            sdb-content-listitem {
                width: 100%; /* lit-virtualizer screws this up */
            }

            sdb-content-listitem.selected {
                font-weight: bold;
                background: rgba(0, 0, 0, 0.1);
            }

            .label {
                color: rgb(115, 115, 115);
                cursor: default;
                display: inline;
                font-family: Roboto, sans-serif;
                font-size: 12px;
                line-height: 16px;
                text-wrap: nowrap;
                white-space-collapse: collapse;
                width: auto;
                -webkit-font-smoothing: antialiased;
                -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            }
        `;
    }

    render() {
        const filter = (filters) => store.dispatch(loadElearnings({ filters, reload: true }));

        return html`
            <div>
                ${this._selectedItem
                    ? html`
                        <div class="label">${translate('selectedElearningItem')}</div>
                        <sdb-content-listitem
                            noHover
                            .graphic=${this._selectedItem.thumbnail}
                            icon=${this._getIcon(this._selectedItem)}
                            headline=${this._selectedItem.title}
                            overline=${getPlayerTypeLabel(this._selectedItem.playerType, this.isGlobal)}
                            subtitle=${this._getSubTitle(this._selectedItem)}>
                        </sdb-content-listitem>
                    ` : nothing}

                <div class="filter-bar">
                    <sdb-search-input
                        placeholder=${translate('search')}
                        value=${this.filters.search}
                        has-border
                        @search=${(e) => { filter({ search: e.detail.searchText }); }}>
                    </sdb-search-input>

                    ${this.isLoadingFilters
                        ? html`<sdb-loading></sdb-loading>`
                        : html`
                            <div class="filters">
                                <vaadin-select
                                    id="supplier_filter"
                                    transparent
                                    label=${translate('supplier')}
                                    value=${toSelectedItemValue(this.filters.supplier)}
                                    @change=${(e) => { filter({ supplier: getSelectedItemValue(e) }); }}
                                    .items=${toSelectItems(this.suppliers, translate('all'))}>
                                </vaadin-select>
                                <vaadin-select
                                    id="playertype_filter"
                                    transparent
                                    label=${translate('playerType')}
                                    value=${toSelectedItemValue(this.filters.playerType)}
                                    @change=${(e) => { filter({ playerType: getSelectedItemValue(e) }); }}
                                    .items=${toSelectItems(this.playerTypes, translate('all'))}>
                                </vaadin-select>
                            </div>
                        `}
                </div>

                <div class="list">
                    ${this.isLoadingInitialList || !this.items
                        ? html`<sdb-loading></sdb-loading>`
                        : html`
                            ${!this.total
                                ? html`<sdb-status-message label=${translate('noElearningsFound')}></sdb-status-message>`
                                : html`
                                    <sdb-content-list id="el_list">
                                        <lit-virtualizer
                                            .items=${this.items}
                                            .renderItem=${(elearning, index) => this._renderElearningListItem(elearning, index)}
                                            .scrollTarget=${document}>
                                        </lit-virtualizer>
                                    </sdb-content-list>
                                `
                            }
                        `
                    }
                <div>
            </div>
        `;
    }

    updated(changes) {
        if (changes.has("launchId") && !!this.launchId) {
            store.dispatch(loadElearning({targetId: this.launchId}));
        }
    }
    firstUpdated() {
        store.dispatch(loadElearnings({ reload: true }));
        store.dispatch(loadFilters({ reload: true }));
    }

    _renderElearningListItem(elearning, index) {
        store.dispatch(loadElearnings({ index }));

        return html`
            <sdb-content-listitem
                .graphic=${elearning.thumbnail}
                icon=${this._getIcon(elearning)}
                headline=${elearning.title}
                overline=${getPlayerTypeLabel(elearning.playerType, this.isGlobal)}
                subtitle=${this._getSubTitle(elearning)}
                @click=${(e) => { this._select(elearning, e.target) }}>
            </sdb-content-listitem>
        `;
    }

    _select(elearning, target) {
        if (this.selectedListItem === target) {
            this._reset();
            this.dispatchEvent(new CustomEvent('selected', {
                detail: {
                    value: null,
                },
                composed: true,
                bubbles: true,
            }));

            return;
        }

        if (this.selectedListItem) {
            this.selectedListItem.classList.remove('selected');
        }

        this.selectedListItem = target;
        this.selectedListItem.classList.add('selected');

        this.dispatchEvent(new CustomEvent('selected', {
            detail: {
                value: elearning,
            },
            composed: true,
            bubbles: true,
        }));
    }

    _reset() {
        if (this.selectedListItem) {
            this.selectedListItem.classList.remove('selected');
        }

        this.selectedListItem = null;
    }

    _getIcon(elearning) {
        switch (elearning?.playerType) {
            case 0: // lti
            case 3: // lti 1.3
                return 'fal fa-square-l';
            case 1: // os
                return 'fal fa-square-o';
            case 2: // scorm
                return 'fal fa-square-s';
            case 4: // youtube
            case 5: // vimeo
                return "fal fa-video";
            default:
            return 'fal fa-question';
        }
    }

    _getSubTitle(elearning) {
        let s = elearning.supplier?.name || '';
        s += elearning.supplier?.name && elearning.description ? ' | ' : '';
        s += elearning.description || '';
        return s;
    }
}


